// tslint:disable-next-line: max-line-length
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  Injector,
  Inject,
  Input,
  Renderer2,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { TooltipDataSharedService } from './tooltip.dataSharedService';
import { isMobileDevice, isTouchEnabled, getFocusableElements, setFocusOnFirstFocusableElement, circulateFocus } from 'axis-components/components/utilities/utilities';

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'tooltipContainer',
  templateUrl: './tooltip.component.html',
  host: { class: 'tooltip-container' }
})
export class TooltipComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() font = 'material';
  public isToggleOnClick: any;
  private content: string;
  private placement: string;
  private axisInclude: string;
  private axisIncludeComponent: any;
  private context: any;
  public tooltipTrigger: string;
  public isOpen: boolean;
  private hostElement: ElementRef;
  private hostElementNative;
  public minwidth: string;
  public width: string;
  public maxHeight: string;
  private mobileMaxHeight = 200;
  private componentRef: any;
  public left: number;
  public right: number;
  public top: number;
  public arrowPos = '';
  public arrowTop: number;
  public arrowLeft: number;
  private eventFlag = true;
  private event: any;
  private bufferSpace = 20;
  private uniqueKey: number;
  private initialOffsetParentObj = {
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0
  };
  public isMobile = false;

  @ViewChild('tooltip', { static: true }) toolTipContent: ElementRef;
  @ViewChild('tooltipContainer', { static: true }) tooltipContainer: ElementRef;
  @ViewChild('container', { read: ViewContainerRef, static: true }) container: ViewContainerRef;
  isTouch: boolean;

  constructor(
    private axisTooltipDataSharedService: TooltipDataSharedService,
    private resolver: ComponentFactoryResolver,
    private renderer: Renderer2,
    private _ChangeDetectorRef: ChangeDetectorRef,
    private injector: Injector,
    @Inject(DOCUMENT) private _document: any
  ) { }

  ngOnInit() {
    this.isMobile = isMobileDevice();
    this.isTouch = isTouchEnabled();
    Object.assign(
      this,
      this.axisTooltipDataSharedService.getData(this.uniqueKey)
    );
    if (this.tooltipTrigger === 'none') {
      this.eventFlag = false;
    } else {
      this.eventFlag = true;
    }
    if (this.axisIncludeComponent) {
      const factory = this.resolver.resolveComponentFactory(
        this.axisIncludeComponent
      );
      this.componentRef = this.container.createComponent(factory);
      this.componentRef.instance.data = this.context;
      let customEvent;
      if (event['clientX'] === 0) {
        customEvent = this.setEvent();
        this.event = customEvent;
      }
      this.componentRef.instance.event = customEvent || event;
      this.componentRef.instance.componentRef = this;
      this.toolTipContent.nativeElement.appendChild(
        this.componentRef.location.nativeElement
      );
    } else {
      this.toolTipContent.nativeElement.innerHTML = this.content;
    }
  }

  setEvent() {
    const event = {
      clientX: 0,
      clientY: 0
    };
    const elementLocation = this.hostElement.nativeElement.getBoundingClientRect();
    if (this.placement === 'top') {
      event.clientX = elementLocation.x + elementLocation.width / 2;
      event.clientY = elementLocation.y;
    } else if (this.placement === 'bottom') {
      event.clientX = elementLocation.x + elementLocation.width / 2;
      event.clientY = elementLocation.y + elementLocation.height;
    } else if (this.placement === 'left') {
      event.clientX = elementLocation.x;
      event.clientY = elementLocation.y + elementLocation.height / 2;
    } else if (this.placement === 'right') {
      event.clientX = elementLocation.x + elementLocation.width;
      event.clientY = elementLocation.y + elementLocation.height / 2;
    }
    return event;
  }

  ngAfterViewInit() {
    this.hostElementNative = this.hostElement.hasOwnProperty('nativeElement')
      ? this.hostElement.nativeElement
      : this.hostElement;

    if (this.isOpen && !this.axisInclude) {
      this.displayTooltip();
    }
  }

  displayTooltip(): void {
    const self = this;
    const calculatePosition = function () {
      return new Promise<void>(function (resolve) {
        self.calculatePosition(self.placement);
        resolve();
      });
    };
    const focusTooltipContainer = function () {
      return new Promise<void>(function (resolve) {
        self.focusTooltipContainer();
        resolve();
      });
    };
    if (this.tooltipTrigger === 'none') {
      calculatePosition().then(focusTooltipContainer);
    } else {
      calculatePosition();
    }
  }

  /**
   * handle focus to circulate within container.
   * @param event as KeyboardEvent
   */
  handleFocus(event: KeyboardEvent) {
    const focusableElements: HTMLElement[] = getFocusableElements(this.tooltipContainer);
    circulateFocus(event, focusableElements, this._document, true, 'axis-tooltip-content');
  }

  focusTooltipContainer() {
    const focusableElements: HTMLElement[] = getFocusableElements(this.tooltipContainer);
    if (focusableElements.length) {
      setFocusOnFirstFocusableElement(focusableElements);
    } else { // if tooltip is empty, then set focus on tooltip and preventDefault on tab press to restrict tab focusing outside tooltip.
      this.tooltipContainer.nativeElement.focus();
      this.renderer.listen(this.tooltipContainer.nativeElement, 'keydown', (event) => {
        const key = event.key.toLocaleLowerCase();
        if (key === 'tab') {
          event.preventDefault();
        }
      });
    }
  }

  close(event: Event): void {
    event.stopPropagation();
    if (this.isOpen) {
      this.axisTooltipDataSharedService.getData(this.uniqueKey).close();
    }
  }

  calculatePosition(placement: string) {
    const tooltipElement = this.tooltipContainer.nativeElement;
    const offsetParent = tooltipElement.offsetParent;
    let posObj;
    if(this.hostElementNative.childNodes[0] && this.hostElementNative.childNodes[0].tagName === 'TABLE') {
      posObj = this.hostElementNative.children[0].getBoundingClientRect();
    } else  {
      posObj =  this.hostElementNative.getBoundingClientRect();
    }
    const tooltipPosObj = tooltipElement.getBoundingClientRect();
    const offsetParentPosObj = offsetParent
      ? offsetParent.getBoundingClientRect()
      : this.initialOffsetParentObj;
    this.smartPositioning(posObj, tooltipPosObj, offsetParentPosObj, placement);
  }

  leftPositioning(posObj: any, tooltipPosObj: any) {
    this.arrowPos = 'left';
    const windowHeight = window.innerHeight;
    this.top = posObj.top + window.pageYOffset + posObj.height/2;
    this.left = posObj.left - tooltipPosObj.width + 5;
    if (this.left < 0) {
      this.left = 0 + this.bufferSpace;
    }

    this.arrowTop = (tooltipPosObj.height * 0.5);
    if (posObj.top < 0) {
      this.arrowTop = 0;
      this.top = this.top - this.arrowTop - 20;
    } else if (this.arrowTop > posObj.top) {
      this.arrowTop = posObj.top + 10;
      this.top = this.top - this.arrowTop + 10;
    } else {
      this.top = this.top - this.arrowTop + 10;
      const totalHeight = posObj.top + tooltipPosObj.height * 0.5;
      if (this.arrowTop > (windowHeight - posObj.bottom) &&  tooltipPosObj.height > posObj.height) {
        const diff = totalHeight - windowHeight;
        this.top = this.top - diff - (posObj.height/2) - (windowHeight - posObj.bottom)/2;
        this.arrowTop = posObj.top + window.pageYOffset - this.top + (posObj.height/2) + 10;
      }
    }
  }

  topPositioning(posObj: any, tooltipPosObj: any) {
    this.arrowPos = 'top';
    if (this.isMobile) {
      const heightDiff = tooltipPosObj.height > this.mobileMaxHeight ? this.mobileMaxHeight + 35  : tooltipPosObj.height;
      tooltipPosObj.height = heightDiff;
    }
    this.top = posObj.top + window.pageYOffset - tooltipPosObj.height;
    this.left = (posObj.left + posObj.width/2) - (tooltipPosObj.width / 2);
    if (this.left < 0) {
      this.left = 0;
    }
    let arrowPoint = tooltipPosObj.width * 0.5 - 12.5;
    this.arrowLeft = arrowPoint;
    if (arrowPoint > posObj.left + posObj.width / 2) {
      this.arrowLeft = posObj.left + posObj.width * 0.5 - 17.5;
      this.left = 5;
    }
  }

  bottomPositioning(posObj: any, tooltipPosObj: any) {
    this.arrowPos = 'bottom';
    this.top = posObj.top + window.pageYOffset + posObj.height - 5;
    this.left = (posObj.left + posObj.width/2) - (tooltipPosObj.width / 2);

    if (this.left < 0) {
      this.left = 0;
    }
    let arrowPoint = tooltipPosObj.width * 0.5 - 12.5;
    this.arrowLeft = arrowPoint;
    if (arrowPoint > posObj.left + posObj.width / 2) {
      this.arrowLeft = posObj.left + posObj.width * 0.5 - 17.5;
      this.left = 5;
    }
  }

  rightPositioning(posObj: any, tooltipPosObj: any) {
    this.arrowPos = 'right';
    const windowHeight = window.innerHeight;
    this.top = posObj.top + window.pageYOffset + posObj.height/2;
    this.left = posObj.left + posObj.width - 5;
    if (this.left < 0) {
      this.left = 0 + this.bufferSpace;
    }

    this.arrowTop = (tooltipPosObj.height * 0.5);

    if (posObj.top < 0) {
      this.arrowTop = 0;
      this.top = this.top - this.arrowTop - 20;
    } else if (this.arrowTop > posObj.top) {
      this.arrowTop = posObj.top + 10;
      this.top = this.top - this.arrowTop + 10;
    } else {
      this.top = this.top - this.arrowTop + 10;
      const totalHeight = posObj.top + tooltipPosObj.height * 0.5;
      if (this.arrowTop > (windowHeight - posObj.bottom) &&  tooltipPosObj.height > posObj.height) {
        const diff = totalHeight - windowHeight;
        this.top = this.top - diff - (posObj.height/2) - (windowHeight - posObj.bottom)/2;
        this.arrowTop = posObj.top + window.pageYOffset - this.top + (posObj.height/2) + 10;
      }
    }
  }

  checkMobilePositioning(posObj: any, tooltipPosObj: any) {
    if (posObj.bottom - tooltipPosObj.height > 0 && posObj.top - 5 - tooltipPosObj.height > 0) {
      this.topPositioning(posObj, tooltipPosObj);
    } else {
      this.bottomPositioning(posObj, tooltipPosObj);
    }
  }

  smartPositioning(posObj: any, tooltipPosObj: any, offsetParentPosObj: any, placement: string) {
    // vertical postioning
    if (this.isMobile) {
      this.isToggleOnClick = true;
      this.maxHeight = this.mobileMaxHeight + 'px';
      this.width = null;
      if (placement === 'bottom') {
        if (posObj.bottom + tooltipPosObj.height < window.innerHeight) {
          this.bottomPositioning(posObj, tooltipPosObj);
        } else if (posObj.bottom - tooltipPosObj.height > 0) {
          this.topPositioning(posObj, tooltipPosObj);
        }
      } else if (placement === 'left' || placement === 'right' || placement === 'top') {
        this.checkMobilePositioning(posObj, tooltipPosObj);
      } else {
        this.checkMobilePositioning(posObj, tooltipPosObj);
      }
    } else {
      if (placement === 'top') {
        if (posObj.bottom - tooltipPosObj.height > 0 && posObj.top - 5 - tooltipPosObj.height > 0) {
          this.topPositioning(posObj, tooltipPosObj);
        } else if (posObj.bottom + tooltipPosObj.height < window.innerHeight) {
          this.bottomPositioning(posObj, tooltipPosObj);
        } else if (posObj.right + tooltipPosObj.width < window.innerWidth) {
          this.rightPositioning(posObj, tooltipPosObj);
        } else {
          this.leftPositioning(posObj, tooltipPosObj);
        }
      } else if (placement === 'bottom') {
        if (posObj.bottom + tooltipPosObj.height < window.innerHeight) {
          this.bottomPositioning(posObj, tooltipPosObj);
        } else if (posObj.bottom - tooltipPosObj.height > 0 &&  posObj.top - 5 - tooltipPosObj.height > 0) {
          this.topPositioning(posObj, tooltipPosObj);
        } else if (posObj.right + tooltipPosObj.width < window.innerWidth) {
          this.rightPositioning(posObj, tooltipPosObj);
        } else {
          this.leftPositioning(posObj, tooltipPosObj);
        }
      } else if (placement === 'left') {
        if (posObj.left > tooltipPosObj.width) {
          this.leftPositioning(posObj, tooltipPosObj);
        } else if (posObj.right + tooltipPosObj.width < window.innerWidth) {
          this.rightPositioning(posObj, tooltipPosObj);
        } else if (posObj.bottom + tooltipPosObj.height < window.innerHeight) {
          this.bottomPositioning(posObj, tooltipPosObj);
        } else {
          this.topPositioning(posObj, tooltipPosObj);
        }
      } else if (placement === 'right') {
        if (posObj.right + tooltipPosObj.width < window.innerWidth) {
          this.rightPositioning(posObj, tooltipPosObj);
        } else if (posObj.left > tooltipPosObj.width) {
          this.leftPositioning(posObj, tooltipPosObj);
        } else if (posObj.bottom + tooltipPosObj.height < window.innerHeight) {
          this.bottomPositioning(posObj, tooltipPosObj);
        } else {
          this.topPositioning(posObj, tooltipPosObj);
        }
      }
    }
    if (!this._ChangeDetectorRef['destroyed']) {
      this._ChangeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy() {
    this.axisTooltipDataSharedService.destroyData(this.uniqueKey);
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }
}
